import { FormControl, FormHelperText, Grid } from '@vapor/material'
import { LocalizedString, NumberField } from 'hic-components'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RateFragment } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservedResourceF } from '../../../../../types/reservation-detail.types'
import { GuestsSchema } from '../edit-rr.schema'
import { GuestsError } from '../edit-rr.types'
import { HicFormLabel } from 'src/app/components/form-fields/hic-form-label.styled'

export type GuestsInputProps = {
  reservedResource: ReservedResourceF
  rate: RateFragment | null
  value: GuestsSchema
  onChange: (value: GuestsSchema) => void
  error?: GuestsError
  disabled?: boolean
}

type Child = {
  childId: string
  noOccupancy: boolean
  description: LocalizedString
}

export default function GuestsInput({ rate, reservedResource, value, onChange, error, disabled }: GuestsInputProps) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const children: Child[] = useMemo(() => {
    const childDiscount = rate ? rate.childRule.childDiscount ?? [] : reservedResource.rate.childRule.childDiscount ?? []
    return childDiscount
      .map((cd) => ({
        childId: cd.child.id, //
        noOccupancy: cd.child.noOccupancy,
        description: cd.child.taxonomy.description,
      }))
      .reverse()
  }, [rate, reservedResource, value])

  useEffect(() => {
    const newValue: GuestsSchema = {
      adults: value.adults, //
      children: children.map((c) => ({
        childId: c.childId, //
        noOccupancy: c.noOccupancy,
        quantity: value.children.find((cc) => cc.childId === c.childId)?.quantity ?? null,
      })),
    }
    if (JSON.stringify(newValue) !== JSON.stringify(value)) {
      onChange(newValue)
    }
  }, [onChange, JSON.stringify(value), JSON.stringify(children)])

  const getChildQuantity = useCallback(
    (childId: string) => {
      return value.children.find((c) => c.childId === childId)?.quantity ?? null
    },
    [value],
  )

  const onChildQuantityChange = useCallback(
    (quantity: number | null, childId: string) => {
      const newChildren = [...value.children]
      const index = newChildren.findIndex((c) => c.childId === childId)
      if (index !== -1) {
        newChildren[index] = { ...newChildren[index], quantity }
      } else {
        const c = children.find((c) => c.childId === childId)
        if (!c) return
        newChildren.push({ childId: c.childId, noOccupancy: c.noOccupancy, quantity })
      }
      onChange({ ...value, children: newChildren })
    },
    [value, onChange],
  )

  const guestsError = useMemo(() => (error && 'type' in error ? error : undefined), [error])
  const adultsError = useMemo(() => (error && 'adults' in error ? error.adults : undefined), [error])
  const childrenError = useMemo(() => (error && 'children' in error ? error.children : undefined), [error])
  const getChildError = useCallback(
    (childId: string) => {
      const index = value.children.findIndex((c) => c.childId === childId)
      if (index <= -1) return undefined
      const e = childrenError?.[index]
      return e?.childId ?? e?.quantity ?? e?.noOccupancy
    },
    [value, childrenError],
  )
  return (
    <Grid item xs={12} container spacing={1}>
      <Grid item xs={3}>
        <FormControl fullWidth error={adultsError || guestsError ? true : false}>
          <HicFormLabel error={adultsError || guestsError ? true : false}>{t('pms.booking.reservation_new.adults')}</HicFormLabel>
          <NumberField //
            size="small"
            value={value.adults}
            onChange={(adults) => onChange({ ...value, adults: adults })}
            error={adultsError || guestsError ? true : false}
            disabled={disabled}
          />
          {(adultsError?.message ? true : false) && <FormHelperText error>{adultsError?.message}</FormHelperText>}
        </FormControl>
      </Grid>
      {children.map((c) => (
        <Grid key={c.childId} item xs={3}>
          <FormControl fullWidth error={getChildError(c.childId) || (guestsError && !c.noOccupancy) ? true : false}>
            <HicFormLabel error={getChildError(c.childId) || (guestsError && !c.noOccupancy) ? true : false}>{localizeString(c.description)}</HicFormLabel>
            <NumberField //
              size="small"
              value={getChildQuantity(c.childId)}
              onChange={(quantity) => onChildQuantityChange(quantity, c.childId)}
              error={getChildError(c.childId) || (guestsError && !c.noOccupancy) ? true : false}
              disabled={disabled}
            />
            {(getChildError(c.childId)?.message ? true : false) && <FormHelperText error>{getChildError(c.childId)?.message}</FormHelperText>}
          </FormControl>
        </Grid>
      ))}
      {(guestsError?.message ? true : false) && <FormHelperText error>{guestsError?.message}</FormHelperText>}
    </Grid>
  )
}
